import { premiumReportPlanMap } from "@ishvaram/domain-data";
import type { CommerceChannel, CommerceSystem } from "@/lib/commerce-routing";
import type { ReportIntentConfig, ReportIntentSlug } from "@/lib/report-intent-data";
import { REPORT_INTENT_PAGES } from "@/lib/report-intent-data";
import type { ReportPlanId } from "@/lib/report-plan-id";
import { WINBACK_PLAN_IDS } from "@/lib/report-plan-id";
import { REPORT_CONTENT } from "@/lib/reports-content";
import type { SEOReportPlanId } from "@/lib/seo-intents";
import {
  ISHVARAM_COMMERCE_SYSTEM,
  ISHVARAM_REPORT_CHECKOUT_CHANNEL,
} from "@/lib/commerce-attribution";

export interface ReportIntentOffer {
  planId: SEOReportPlanId;
  priceInr: number;
  headline: string;
  subhead: string;
  source: string;
  collectInputsHref: string;
  collectInputsLabel: string;
  collectInputsNote: string;
  analyticsContext: {
    checkoutChannel: CommerceChannel;
    commerceSystem: CommerceSystem;
    intent: string;
    sourcePath: string;
    placement: string;
    customerProblem: string;
    urgency: string;
    confidence: number;
    routeRankScore: number;
    routeRankReason: string;
    rankedOfferOrder: string[];
  };
}

export function reportIntentOffer(slug: ReportIntentSlug): ReportIntentOffer {
  const page = REPORT_INTENT_PAGES[slug];
  const report = REPORT_CONTENT[page.reportSlug];
  return {
    planId: report.plan_id,
    priceInr: report.price_inr,
    headline: page.offerHeadline,
    subhead: page.offerSubhead,
    source: `report-landing:${slug}`,
    collectInputsHref: reportIntentCollectInputsHref(page, report.plan_id),
    collectInputsLabel: page.collectInputsLabel,
    collectInputsNote:
      page.inputSurface === "matching"
        ? "Marriage Report needs both charts first. Checkout opens after Kundali Milan has the required inputs."
        : "Birth details come first. Checkout opens after the chart has the inputs needed for your report.",
    analyticsContext: {
      checkoutChannel: ISHVARAM_REPORT_CHECKOUT_CHANNEL,
      commerceSystem: ISHVARAM_COMMERCE_SYSTEM,
      intent: page.intent,
      sourcePath: `/${page.slug}`,
      placement: "report_landing_primary_offer",
      customerProblem: page.customerProblem,
      urgency: "high",
      confidence: 0.9,
      routeRankScore: 100,
      routeRankReason: `report_landing_${report.plan_id}`,
      rankedOfferOrder: [report.plan_id],
    },
  };
}

/** A ₹49 win-back offer: a pricing variant of a base report-intent offer, sold
 * ONLY on the re-entry nudge surface. The planId is the win-back catalog plan_id
 * (pins ₹49 at checkout; the backend resolves the report_type to the base twin,
 * so the report is identical), the price + strike anchor both come from the
 * governed catalog map (never hand-typed — closes the display-vs-charge drift
 * class), and the analytics carry the win-back plan_id for path-based readout. */
export interface WinbackReportOffer extends Omit<ReportIntentOffer, "planId"> {
  /** The win-back checkout plan_id (₹49 pricing variant). */
  planId: ReportPlanId;
  /** The base full price rendered struck-through beside ₹49 (governed anchor). */
  strikePriceInr: number;
}

export type WinbackReportSlug = keyof typeof WINBACK_PLAN_IDS;

export function winbackReportOffer(slug: WinbackReportSlug): WinbackReportOffer {
  const base = reportIntentOffer(slug);
  const winbackPlanId = WINBACK_PLAN_IDS[slug];
  // Both prices come from the SAME governed catalog map the money path pins from:
  // winback = ₹49, strike = the base report's live full price (a REAL discount).
  const priceInr = premiumReportPlanMap[winbackPlanId].price_inr;
  const strikePriceInr = premiumReportPlanMap[base.planId].price_inr;
  return {
    ...base,
    planId: winbackPlanId,
    priceInr,
    strikePriceInr,
    source: `winback:${slug}`,
    analyticsContext: {
      ...base.analyticsContext,
      placement: "trigger_reentry",
      routeRankReason: `winback_${winbackPlanId}`,
      rankedOfferOrder: [winbackPlanId],
    },
  };
}

/** The win-back plan id the page checks out with (typed for PaidReportCTA). */
export function winbackPlanIdFor(slug: WinbackReportSlug): ReportPlanId {
  return WINBACK_PLAN_IDS[slug];
}

export function reportIntentCollectInputsHref(page: ReportIntentConfig, planId: string): string {
  if (page.inputSurface === "matching") {
    const params = new URLSearchParams({
      source: `/${page.slug}`,
      next_report: planId,
    });
    return `/kundli-matching/#${params.toString()}`;
  }
  const params = new URLSearchParams({
    intent: page.intent,
    source: `/${page.slug}`,
    next_report: planId,
  });
  return `/free-kundali/#${params.toString()}`;
}
